<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { useStore } from "vuex";

import {
  fetcherEvents,
  showServiceNotification,
} from "@/shared/lib/centrifuge";
import { generateRequestUuid } from "@/shared/lib/uuid";
import { toast } from "@/shared/store/alertStore";
import {
  getServiceListWithResponseByCode,
  runChainServiceByIndividual,
} from "@/entities/template";
import { ServiceExecutionStatus } from "@/shared/config/serviceExecutionStatuses";

import { FormSheet } from "@/shared/ui/form-sheet";
import { FormItem, FormLabel, FormControl } from "@/shared/ui/form";
import { Input } from "@/shared/ui/input";
import { Skeleton } from "@/shared/ui/skeleton";
import Button from "@/shared/ui/button/Button.vue";
import { Spinner } from "@/shared/ui/spinner";
import { RadioGroup, Radio } from "@/shared/ui/radio";
import { SectionBlock } from "@/shared/ui/section-block";
import { DatePicker } from "@/shared/ui/date-picker";

const { t } = useI18n();
const route = useRoute();
const store = useStore();

// ИИН авторизованного пользователя (для self-detection).
const selfUserIdentifier = computed(
  () => store.state.self_user?.identifier || ""
);
// БИН из ЭЦП авторизованного пользователя (если подписан сертификатом ЮЛ).
const selfUserBin = computed(() => store.state.self_user?.subject?.bin || "");

const props = defineProps({
  description: {
    type: String,
    default: "",
  },
  participantType: {
    type: String,
    default: null, // "INDIVIDUAL" | "LEGAL"
  },
  selectedRole: {
    type: Object,
    default: () => ({}),
  },
  editingParticipant: {
    type: Object,
    default: null,
  },
  execution: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(["close", "save"]);

const visible = ref(false);

const isEdit = computed(() => !!props.editingParticipant?.uuid);

const txUuid = computed(() => route.params.id);

// Признак: участник действует от имени ЮЛ (верхний переключатель формы).
// По умолчанию «Да» для LEGAL-роли и при редактировании ЮЛ-участника.
const actsForLegal = ref(
  Boolean(
    props.participantType === "LEGAL" ||
      props.editingParticipant?.participant_type === "LEGAL" ||
      props.editingParticipant?.params?.company_bin ||
      props.editingParticipant?.params?.company_name
  )
);

// Прокси для RadioGroup (значения приводятся к строке).
const actsForLegalChoice = computed({
  get: () => (actsForLegal.value ? "yes" : "no"),
  set: (v) => {
    actsForLegal.value = v === "yes";
  },
});

const roleName = computed(() => {
  if (isEdit.value) {
    return (
      props.editingParticipant?.participant_type_code_info?.name ||
      props.selectedRole?.name ||
      t("Участник")
    );
  }
  return props.selectedRole?.name || t("Участник");
});

const title = computed(
  () =>
    `${roleName.value} — ${actsForLegal.value ? t("Юр лицо") : t("Физ лицо")}`
);

// identifier — всегда ИИН физлица. БИН компании только в params.company_bin.
// Полный набор «Сведения о лице»: ФИО + ИИН + дата рождения + адрес рег. + адрес рождения.
const initialReg = props.editingParticipant?.params?.reg_address || {};
const initialBirth = props.editingParticipant?.params?.birth_address || {};

const participantForm = ref({
  participant_type_code:
    props.editingParticipant?.participant_type_code ||
    props.selectedRole?.code ||
    null,
  identifier: props.editingParticipant?.identifier || "",
  first_name: props.editingParticipant?.first_name || "",
  last_name: props.editingParticipant?.last_name || "",
  middle_name: props.editingParticipant?.middle_name || "",
  birth_date: props.editingParticipant?.params?.birth_date || "",
  reg_address: {
    country: initialReg.country || "",
    region: initialReg.region || "",
    district: initialReg.district || "",
    city: initialReg.city || "",
    street: initialReg.street || "",
    building: initialReg.building || "",
    corpus: initialReg.corpus || "",
    flat: initialReg.flat || "",
  },
  birth_address: {
    country: initialBirth.country || "",
    region: initialBirth.region || "",
    district: initialBirth.district || "",
    city: initialBirth.city || "",
  },
});

// Self-detection: ИИН формы совпадает с ИИН авторизованного пользователя.
// В режиме ЮЛ identifier = ИИН руководителя, поэтому self-детект работает и там.
const isSelf = computed(() => {
  const id = (participantForm.value.identifier || "").trim();
  const selfId = (selfUserIdentifier.value || "").trim();
  return Boolean(id && selfId && id === selfId);
});

// Название организации: из ответа GBDULBasic, из сертификата (/me) или вручную.
const legalCompanyName = ref(
  props.editingParticipant?.params?.company_name || ""
);

// ===== Сервисы ГБД =====
const SERVICE_GBDUL = "GBDULBasic";
const SERVICE_GBDFL = "GBDFL";

const currentSteps = computed(() => {
  const map = props.execution?.steps_entity_types || {};
  const individualSteps = map.INDIVIDUAL || [];
  const legalSteps = map.LEGAL || [];
  return [...individualSteps, ...legalSteps];
});

const gbdulStep = computed(
  () => currentSteps.value.find((s) => s.code === SERVICE_GBDUL) || null
);
const gbdflStep = computed(
  () => currentSteps.value.find((s) => s.code === "GBDFLBasic") || null
);

const canSearchLegal = computed(() => Boolean(gbdulStep.value));

const warningMessage = computed(() =>
  canSearchLegal.value
    ? null
    : t("У вас нет доступа к поиску данного типа лиц.")
);

// ИИН руководителя введён полностью (12 цифр), но не совпадает с ИИН из ЭЦП
// → нельзя добавить чужую организацию (ошибку не показываем во время набора).
const directorIinMismatch = computed(() => {
  const iin = (participantForm.value.identifier || "").trim();
  const subjIin = (store.state.self_user?.subject?.iin || "").trim();
  return Boolean(iin.length === 12 && subjIin && iin !== subjIin);
});

// State по образцу ServiceExecutionBlock.vue
const serviceLoading = ref(false);
const activeService = ref(null); // схема GBDULBasic для динамических полей
const dynamicForm = ref({
  business_identifier:
    props.editingParticipant?.params?.company_bin || selfUserBin.value || "",
});
const serviceResponseData = ref(null);
const searching = ref(false);
const searchCompleted = ref(false);
const pendingRequestUuid = ref(null);
// Какой сервис инициировал текущий запрос — используется и для шаблона,
// и для ветвления в handleWsResponse (живёт до конца searching).
const lastSearchCode = ref(null);
let unsubscribeWs = null;
let requestStartedAt = 0;
const MIN_SKELETON_MS = 1000;

function initDynamicForm(properties = {}) {
  return Object.fromEntries(
    Object.entries(properties || {}).map(([key, schema]) => [
      key,
      schema.default ?? (schema.type === "boolean" ? false : ""),
    ])
  );
}

function checkHasKeyDynamicForm(key) {
  return Object.keys(dynamicForm.value).includes(key);
}

async function loadGbdulSchema() {
  serviceResponseData.value = null;
  searchCompleted.value = false;
  searching.value = false;
  lastSearchCode.value = null;
  pendingRequestUuid.value = null;
  if (unsubscribeWs) {
    unsubscribeWs();
    unsubscribeWs = null;
  }

  try {
    serviceLoading.value = true;
    const data = await getServiceListWithResponseByCode(SERVICE_GBDUL);
    if (data?.success && data.payload) {
      activeService.value = data.payload;
      dynamicForm.value = initDynamicForm(
        data.payload.request?.properties || {}
      );
      searchCompleted.value = false;
    } else {
      activeService.value = null;
      dynamicForm.value = {};
    }
  } catch (e) {
    console.error("loadGbdulSchema error:", e);
    activeService.value = null;
    dynamicForm.value = {};
  } finally {
    serviceLoading.value = false;
  }

  // Подставляем значения: приоритет — данные участника, затем сертификат (/me).
  if (!dynamicForm.value.business_identifier) {
    dynamicForm.value.business_identifier =
      props.editingParticipant?.params?.company_bin || "";
  }
  prefillFromSubject();
  if (checkHasKeyDynamicForm("person_identifier")) {
    dynamicForm.value["person_identifier"] = participantForm.value.identifier;
  }
}

// Предзаполнение полей ЗАПРОСА из сертификата (/me) — чтобы отправить запрос
// в ГБД. Название/ФИО берём ИСКЛЮЧИТЕЛЬНО из ответа сервиса.
// БИН — всегда из своей ЭЦП (можно добавить только свою организацию).
// ИИН руководителя — подставляем из ЭЦП, но поле редактируемое (см. валидацию).
function prefillFromSubject() {
  const subject = store.state.self_user?.subject || {};
  if (subject.bin) dynamicForm.value.business_identifier = String(subject.bin);
  if (!isEdit.value && subject.iin && !participantForm.value.identifier) {
    participantForm.value.identifier = String(subject.iin);
  }
}

function ensureWsSubscribed() {
  if (!unsubscribeWs) {
    unsubscribeWs = fetcherEvents.on(handleWsResponse);
  }
}

// === Поиск ЮЛ (GBDULBasic) ===
async function searchCompany() {
  if (!gbdulStep.value) {
    toast.error(t("Сервис ГБД ЮЛ недоступен"));
    return;
  }
  if (!props.execution?.uuid) return;

  if (!activeService.value) {
    await loadGbdulSchema();
    if (!activeService.value) return;
  }

  if (checkHasKeyDynamicForm("person_identifier")) {
    dynamicForm.value["person_identifier"] = participantForm.value.identifier;
  }

  const bin = dynamicForm.value?.business_identifier || "";
  if (!bin || bin.length !== 12) {
    toast.error(t("Введите корректный БИН (12 цифр)"));
    return;
  }

  serviceResponseData.value = null;
  searchCompleted.value = false;

  searching.value = true;
  lastSearchCode.value = SERVICE_GBDUL;
  requestStartedAt = Date.now();

  try {
    // Подписываемся ДО запуска — чтобы не пропустить быстрый WS-ответ (гонка).
    const clientRequestUuid = generateRequestUuid();
    pendingRequestUuid.value = clientRequestUuid;
    ensureWsSubscribed();

    const result = await runChainServiceByIndividual(
      props.execution.uuid,
      gbdulStep.value.uuid,
      txUuid.value,
      { ...dynamicForm.value, request_uuid: clientRequestUuid }
    );

    if (result?.status === 403 || result?.payload?.has_access === false) {
      cleanupWs();
      toast.error(t("Нет доступа к сервису ГБД ЮЛ"), { duration: 5000 });
      searching.value = false;
      lastSearchCode.value = null;
      searchCompleted.value = true;
      return;
    }

    const chainRunData = result?.payload?.chain_run?.payload || result?.payload;

    if (result?.success && chainRunData?.run) {
      return;
    }

    cleanupWs();
    serviceResponseData.value = null;
    searching.value = false;
    lastSearchCode.value = null;
    searchCompleted.value = true;
  } catch (e) {
    console.error("[AddParticipantToInscription] searchCompany error:", e);
    cleanupWs();
    searching.value = false;
    lastSearchCode.value = null;
    searchCompleted.value = true;
  } finally {
    if (!pendingRequestUuid.value) {
      setTimeout(() => {
        searching.value = false;
        lastSearchCode.value = null;
      }, 800);
    }
  }
}

// === Получить мои данные из ГБД ФЛ (только для self) ===
async function fetchSelfIndividualData() {
  if (!gbdflStep.value) {
    toast.error(t("Сервис ГБД ФЛ недоступен"));
    return;
  }
  if (!props.execution?.uuid) return;

  const iin = participantForm.value.identifier || selfUserIdentifier.value;
  if (!iin || iin !== selfUserIdentifier.value) {
    toast.error(t("Можно получить данные только по своему ИИН"));
    return;
  }

  serviceResponseData.value = null;
  searchCompleted.value = false;
  searching.value = true;
  lastSearchCode.value = SERVICE_GBDFL;
  requestStartedAt = Date.now();

  try {
    // Подписываемся ДО запуска — чтобы не пропустить быстрый WS-ответ (гонка).
    const clientRequestUuid = generateRequestUuid();
    pendingRequestUuid.value = clientRequestUuid;
    ensureWsSubscribed();

    const result = await runChainServiceByIndividual(
      props.execution.uuid,
      gbdflStep.value.uuid,
      txUuid.value,
      { person_identifier: iin, request_uuid: clientRequestUuid }
    );

    if (result?.status === 403 || result?.payload?.has_access === false) {
      cleanupWs();
      toast.error(t("Нет доступа к сервису ГБД ФЛ"), { duration: 5000 });
      searching.value = false;
      lastSearchCode.value = null;
      searchCompleted.value = true;
      return;
    }

    const chainRunData = result?.payload?.chain_run?.payload || result?.payload;
    if (result?.success && chainRunData?.run) {
      return;
    }

    cleanupWs();
    serviceResponseData.value = null;
    searching.value = false;
    lastSearchCode.value = null;
    searchCompleted.value = true;
  } catch (e) {
    console.error(
      "[AddParticipantToInscription] fetchSelfIndividualData error:",
      e
    );
    cleanupWs();
    searching.value = false;
    lastSearchCode.value = null;
    searchCompleted.value = true;
  } finally {
    if (!pendingRequestUuid.value) {
      setTimeout(() => {
        searching.value = false;
        lastSearchCode.value = null;
      }, 800);
    }
  }
}

// Извлечение строкового значения из поля ответа (string | number | {name, name_ru, name_kk}).
function extractText(field) {
  if (field == null) return "";
  if (typeof field === "string" || typeof field === "number") {
    return String(field).trim();
  }
  if (typeof field === "object") {
    return String(field.name || field.name_ru || field.name_kk || "").trim();
  }
  return "";
}

// Маппинг ответа GBDFL в participantForm.
function applyGbdflResponseToForm(data) {
  if (!data || typeof data !== "object") return;

  if (
    !participantForm.value.identifier &&
    (typeof data.iin === "string" || typeof data.iin === "number")
  ) {
    participantForm.value.identifier = String(data.iin);
  }
  if (typeof data.first_name === "string" && data.first_name) {
    participantForm.value.first_name = data.first_name;
  }
  if (typeof data.last_name === "string" && data.last_name) {
    participantForm.value.last_name = data.last_name;
  }
  if (typeof data.middle_name === "string" && data.middle_name) {
    participantForm.value.middle_name = data.middle_name;
  }
  if (typeof data.birth_date === "string" && data.birth_date) {
    participantForm.value.birth_date = data.birth_date;
  }

  const reg = data.reg_address || {};
  participantForm.value.reg_address = {
    country:
      extractText(reg.country) || participantForm.value.reg_address.country,
    region: extractText(reg.region) || participantForm.value.reg_address.region,
    district:
      extractText(reg.district) || participantForm.value.reg_address.district,
    city: extractText(reg.city) || participantForm.value.reg_address.city,
    street: extractText(reg.street) || participantForm.value.reg_address.street,
    building:
      extractText(reg.building) || participantForm.value.reg_address.building,
    corpus: extractText(reg.corpus) || participantForm.value.reg_address.corpus,
    flat: extractText(reg.flat) || participantForm.value.reg_address.flat,
  };

  const birth = data.birth_address || {};
  participantForm.value.birth_address = {
    country:
      extractText(birth.country) || participantForm.value.birth_address.country,
    region:
      extractText(birth.region) || participantForm.value.birth_address.region,
    district:
      extractText(birth.district) ||
      participantForm.value.birth_address.district,
    city: extractText(birth.city) || participantForm.value.birth_address.city,
  };
}

function handleWsResponse(data) {
  if (!lastSearchCode.value) return;
  if (data?.type !== "FETCHER") return;
  // Принимаем только событие СВОЕГО запроса. Без строгого совпадения по
  // request_uuid форма ловила чужие глобальные FETCHER-события и показывала
  // ложную «Организация найдена».
  if (
    !pendingRequestUuid.value ||
    data.request_uuid !== pendingRequestUuid.value
  ) {
    return;
  }

  const wsPayload = data?.payload;
  if (wsPayload?.status === ServiceExecutionStatus.PENDING) return;

  // NOTIFICATION — ожидание подтверждения клиента (КДП), loading продолжается
  if (wsPayload?.status === ServiceExecutionStatus.NOTIFICATION) {
    showServiceNotification(wsPayload);
    return;
  }

  const finishedCode = lastSearchCode.value;
  pendingRequestUuid.value = null;
  if (unsubscribeWs) {
    unsubscribeWs();
    unsubscribeWs = null;
  }

  const elapsed = Date.now() - (requestStartedAt || Date.now());
  const remaining = Math.max(0, MIN_SKELETON_MS - elapsed);

  setTimeout(() => {
    const hasData =
      wsPayload?.status === ServiceExecutionStatus.FINISHED &&
      wsPayload.data &&
      Object.keys(wsPayload.data).length > 0;

    if (finishedCode === SERVICE_GBDUL) {
      if (hasData) {
        serviceResponseData.value = wsPayload.data;

        const nameKeyRegex = /(name|наим|organization|company)/i;
        const foundName = Object.entries(wsPayload.data).find(
          ([k, v]) => typeof v === "string" && v.trim() && nameKeyRegex.test(k)
        );
        if (foundName) legalCompanyName.value = foundName[1];

        const binKeyRegex = /(^bin$|business_id|bin_id)/i;
        const foundBin = Object.entries(wsPayload.data).find(
          ([k, v]) =>
            (typeof v === "string" || typeof v === "number") &&
            String(v).trim() &&
            binKeyRegex.test(k)
        );
        if (foundBin) {
          dynamicForm.value["business_identifier"] = String(foundBin[1]);
        }

        toast.success(t("Данные получены"), {
          description: new Date().toLocaleTimeString(),
          duration: 3000,
        });
        searching.value = false;
        lastSearchCode.value = null;
        searchCompleted.value = true;
      } else {
        // ЮЛ не найдено — данные только из сервиса, ручной ввод недоступен.
        serviceResponseData.value = null;
        const errorMsg =
          wsPayload?.error_description || t("Организация не найдена");
        toast.error(errorMsg, { duration: 5000 });
        searching.value = false;
        lastSearchCode.value = null;
        searchCompleted.value = true;
      }
    } else if (finishedCode === SERVICE_GBDFL) {
      if (hasData) {
        serviceResponseData.value = wsPayload.data;
        applyGbdflResponseToForm(wsPayload.data);
        toast.success(t("Ваши данные получены"), {
          description: new Date().toLocaleTimeString(),
          duration: 3000,
        });
      } else {
        serviceResponseData.value = null;
        const errorMsg =
          wsPayload?.error_description ||
          t("Не удалось получить данные из ГБД ФЛ");
        toast.error(errorMsg, { duration: 5000 });
      }
      searching.value = false;
      lastSearchCode.value = null;
      searchCompleted.value = true;
    }
  }, remaining);
}

function cleanupWs() {
  pendingRequestUuid.value = null;
  if (unsubscribeWs) {
    unsubscribeWs();
    unsubscribeWs = null;
  }
}

// Переключение режима ЮЛ: при включении — схема + префилл из сертификата,
// при выключении — чистим состояние поиска, чтобы не оставалось stale-данных.
watch(actsForLegal, async (val) => {
  if (val) {
    serviceResponseData.value = null;
    searchCompleted.value = false;
    if (canSearchLegal.value && !activeService.value) {
      await loadGbdulSchema();
    } else {
      prefillFromSubject();
    }
  } else {
    cleanupWs();
    serviceResponseData.value = null;
    searchCompleted.value = false;
    legalCompanyName.value = "";
  }
});

// Синхронизация: ИИН формы физлица -> dynamicForm.person_identifier.
watch(
  () => participantForm.value.identifier,
  (val) => {
    if (
      dynamicForm.value &&
      "person_identifier" in dynamicForm.value &&
      dynamicForm.value.person_identifier !== val
    ) {
      dynamicForm.value.person_identifier = val || "";
    }
  }
);

// === Валидация: какие поля обязательны ===
// Ручной ввод должника: обязательны все поля, кроме корпуса/квартиры
// (их может не быть для частного дома).
const REQUIRED_REG_KEYS = [
  "country",
  "region",
  "district",
  "city",
  "street",
  "building",
  "flat",
];
const REQUIRED_BIRTH_KEYS = ["country", "region", "district", "city"];

const isSaveDisabled = computed(() => {
  const iin = participantForm.value.identifier || "";
  if (!iin || iin.length !== 12) return true;

  if (actsForLegal.value) {
    // ЮЛ: добавить можно только свою организацию (нужна ЭЦП руководителя),
    // БИН и название — из ответа сервиса. ФИО руководителя не требуется.
    if (!selfUserBin.value) return true;
    if (directorIinMismatch.value) return true;
    const bin = dynamicForm.value?.business_identifier || "";
    if (!bin || bin.length !== 12) return true;
    if (!legalCompanyName.value) return true;
    return false;
  }

  // INDIVIDUAL: ФИО обязательны; паспорт/адрес — только для НЕ-self (ручной ввод).
  if (!participantForm.value.first_name || !participantForm.value.last_name) {
    return true;
  }
  if (!isSelf.value) {
    // Должник вводится вручную — обязательны все поля.
    if (!participantForm.value.middle_name) return true;
    if (!participantForm.value.birth_date) return true;
    for (const key of REQUIRED_REG_KEYS) {
      if (!participantForm.value.reg_address[key]) return true;
    }
    for (const key of REQUIRED_BIRTH_KEYS) {
      if (!participantForm.value.birth_address[key]) return true;
    }
  }
  return false;
});

function close() {
  cleanupWs();
  visible.value = false;
  emit("close");
}

function pruneEmpty(obj) {
  const out = {};
  Object.entries(obj || {}).forEach(([k, v]) => {
    if (v !== "" && v != null) out[k] = v;
  });
  return out;
}

// Shape совпадает с GBDFL-ответом и с тем, что нормализуется в
// shared/serviceResponse — чтобы service_fields.manual читался тем же путём,
// что и реальные сервисные данные (см. InscriptionRedactor → onInsertSavedFields).
function buildManualServiceFields() {
  const dict = (v) => (v ? { name: v, name_ru: v, name_kk: v } : null);
  const f = participantForm.value;
  return {
    iin: f.identifier || null,
    first_name: f.first_name || null,
    last_name: f.last_name || null,
    middle_name: f.middle_name || null,
    birth_date: f.birth_date || null,
    reg_address: {
      country: dict(f.reg_address.country),
      region: dict(f.reg_address.region),
      district: dict(f.reg_address.district),
      city: f.reg_address.city || null,
      street: f.reg_address.street || null,
      building: f.reg_address.building || null,
      corpus: f.reg_address.corpus || null,
      flat: f.reg_address.flat || null,
    },
    birth_address: {
      country: dict(f.birth_address.country),
      region: dict(f.birth_address.region),
      district: dict(f.birth_address.district),
      city: f.birth_address.city || null,
    },
  };
}

function save() {
  if (isSaveDisabled.value) return;

  const baseParams = { ...(props.editingParticipant?.params || {}) };

  if (actsForLegal.value) {
    // Сохранение как LEGAL: компания + представитель-физлицо (ФИО+ИИН).
    const payload = {
      participant_type: "LEGAL",
      participant_type_code: participantForm.value.participant_type_code,
      identifier: participantForm.value.identifier,
      first_name: participantForm.value.first_name,
      last_name: participantForm.value.last_name,
      middle_name: participantForm.value.middle_name,
      params: {
        ...baseParams,
        company_bin: dynamicForm.value?.business_identifier || "",
        company_name: legalCompanyName.value,
      },
    };
    return isEdit.value
      ? emit("save", { uuid: props.editingParticipant.uuid, payload })
      : emit("save", payload);
  }

  // INDIVIDUAL — полный паспортный набор.
  delete baseParams.company_bin;
  delete baseParams.company_name;

  const regClean = pruneEmpty(participantForm.value.reg_address);
  const birthClean = pruneEmpty(participantForm.value.birth_address);

  const params = { ...baseParams };
  if (participantForm.value.birth_date) {
    params.birth_date = participantForm.value.birth_date;
  }
  if (Object.keys(regClean).length) params.reg_address = regClean;
  if (Object.keys(birthClean).length) params.birth_address = birthClean;

  const payload = {
    participant_type: "INDIVIDUAL",
    participant_type_code: participantForm.value.participant_type_code,
    identifier: participantForm.value.identifier,
    first_name: participantForm.value.first_name,
    last_name: participantForm.value.last_name,
    middle_name: participantForm.value.middle_name,
  };
  if (Object.keys(params).length) payload.params = params;

  // service_fields.manual — снимок ручного ввода для нотариуса вниз по потоку.
  // Self получает данные через реальный GBDFL — backend сам положит service_fields.GBDFL.
  if (!isSelf.value) {
    const existingServiceFields =
      props.editingParticipant?.service_fields || {};
    payload.service_fields = {
      ...existingServiceFields,
      manual: buildManualServiceFields(),
    };
  }

  if (isEdit.value) {
    emit("save", { uuid: props.editingParticipant.uuid, payload });
  } else {
    emit("save", payload);
  }
}

onMounted(async () => {
  visible.value = true;
  if (actsForLegal.value) {
    if (canSearchLegal.value) await loadGbdulSchema();
    prefillFromSubject();
  }

  // Редактирование: показываем сохранённые данные как карточку (лицо/организация).
  if (isEdit.value) {
    searchCompleted.value = true;
    if (actsForLegal.value) {
      const bin = props.editingParticipant?.params?.company_bin || "";
      if (legalCompanyName.value || bin) {
        serviceResponseData.value = { name: legalCompanyName.value, bin };
      }
    }
  }
});

onBeforeUnmount(() => {
  cleanupWs();
});
</script>

<template>
  <FormSheet
    v-model:open="visible"
    :title="title"
    :description="description"
    :width="500"
    @close="close"
  >
    <div class="w-full h-full flex flex-col gap-5">
      <!-- Действует от имени юридического лица (верхний выбор) -->
      <FormItem>
        <FormLabel class="text-muted-foreground text-sm">{{
          $t("Действует от имени юридического лица")
        }}</FormLabel>
        <FormControl>
          <RadioGroup
            v-model="actsForLegalChoice"
            class="flex items-center gap-6"
          >
            <Radio value="no">{{ $t("Нет") }}</Radio>
            <Radio value="yes">{{ $t("Да") }}</Radio>
          </RadioGroup>
        </FormControl>
      </FormItem>

      <!-- ===== Режим физлица ===== -->
      <template v-if="!actsForLegal">
        <SectionBlock class="pt-3">
          <FormItem float>
            <FormLabel>
              <span class="inline-flex items-center gap-2 flex-wrap">
                <span>
                  {{ $t("ИИН") }}
                  <span class="text-red-500">*</span>
                </span>
              </span>
            </FormLabel>
            <FormControl>
              <Input v-model="participantForm.identifier" maxlength="12" />
            </FormControl>
          </FormItem>

          <FormItem
            v-if="
              !actsForLegal &&
              !isSelf &&
              participantForm.identifier?.length === 12
            "
            float
          >
            <FormLabel>
              {{ $t("Фамилия") }}
              <span class="text-red-500">*</span>
            </FormLabel>
            <FormControl>
              <Input v-model="participantForm.last_name" />
            </FormControl>
          </FormItem>

          <FormItem
            v-if="
              !actsForLegal &&
              !isSelf &&
              participantForm.identifier?.length === 12
            "
            float
          >
            <FormLabel>
              {{ $t("Имя") }}
              <span class="text-red-500">*</span>
            </FormLabel>
            <FormControl>
              <Input v-model="participantForm.first_name" />
            </FormControl>
          </FormItem>

          <FormItem
            v-if="
              !actsForLegal &&
              !isSelf &&
              participantForm.identifier?.length === 12
            "
            float
          >
            <FormLabel>
              {{ $t("Отчество") }}
              <span class="text-red-500">*</span>
            </FormLabel>
            <FormControl>
              <Input v-model="participantForm.middle_name" />
            </FormControl>
          </FormItem>

          <FormItem
            v-if="
              !actsForLegal &&
              !isSelf &&
              participantForm.identifier?.length === 12
            "
          >
            <FormLabel>
              {{ $t("Дата рождения") }}
              <span class="text-red-500">*</span>
            </FormLabel>
            <FormControl>
              <DatePicker
                v-model="participantForm.birth_date"
                :placeholder="$t('Дата рождения')"
              />
            </FormControl>
          </FormItem>

          <!-- Self-flow: кнопка запроса ГБД ФЛ — только пока не включён режим ЮЛ -->
          <template v-if="!actsForLegal && isSelf">
            <!-- Сервис недоступен в текущей записи -->
            <div
              v-if="!gbdflStep"
              class="flex gap-3 p-3 rounded-lg bg-yellow-100 items-start mt-1"
            >
              <svg-icon
                name="huge-warning"
                class="text-yellow-500 w-5 h-5 flex-shrink-0"
              />
              <span class="text-sm">{{ $t("Сервис ГБД ФЛ недоступен") }}</span>
            </div>

            <template v-else>
              <Button
                variant="outline"
                class="w-full flex items-center justify-center mt-1"
                :disabled="searching || serviceLoading"
                @click="fetchSelfIndividualData"
              >
                <template v-if="searching && lastSearchCode === SERVICE_GBDFL">
                  <Spinner size="sm" class="mr-2" />
                  {{ $t("Получаем данные...") }}
                </template>
                <template v-else>
                  {{ $t("Запросить данные") }}
                </template>
              </Button>

              <div class="flex flex-col gap-4 mt-2">
                <template v-if="searching && lastSearchCode === SERVICE_GBDFL">
                  <p class="text-muted-foreground text-sm font-medium">
                    {{ $t("Идет получение данных из ГБД ФЛ...") }}
                  </p>
                  <Skeleton class="h-20 w-full" />
                </template>

                <template
                  v-else-if="
                    searchCompleted &&
                    lastSearchCode === null &&
                    !participantForm.first_name
                  "
                >
                  <p class="text-muted-foreground text-sm font-medium">
                    {{ $t("Лицо не найдено") }}
                  </p>
                </template>
              </div>
            </template>
          </template>

          <!-- Карточка ФИО: видна и в режиме физлица, и при действии от ЮЛ.
               Показываем только после успешного запроса в текущей сессии. -->
          <div
            v-if="
              (isSelf || isEdit) &&
              participantForm.first_name &&
              participantForm.last_name &&
              searchCompleted
            "
            class="flex flex-col gap-2 mt-2"
          >
            <p class="text-muted-foreground text-sm font-medium">
              {{ $t("Лицо найдено") }}
            </p>
            <div
              class="p-4 rounded-2xl border border-border flex gap-4 items-center"
            >
              <div
                class="w-16 h-[78px] rounded-xl border border-border flex items-center justify-center bg-muted flex-shrink-0"
              >
                <svg-icon
                  name="huge-user"
                  class="w-6 h-6 text-muted-foreground"
                />
              </div>
              <div class="flex flex-col gap-2 flex-1 min-w-0">
                <div>
                  <p class="text-muted-foreground text-xs">
                    {{ $t("ФИО") }}
                  </p>
                  <p class="text-foreground text-sm break-words">
                    {{
                      [
                        participantForm.last_name,
                        participantForm.first_name,
                        participantForm.middle_name,
                      ]
                        .filter(Boolean)
                        .join(" ")
                    }}
                  </p>
                </div>

                <div v-if="participantForm.identifier">
                  <p class="text-muted-foreground text-xs">
                    {{ $t("ИИН") }}
                  </p>
                  <p class="text-foreground text-sm">
                    {{ participantForm.identifier }}
                  </p>
                </div>

                <div v-if="participantForm.birth_date && !actsForLegal">
                  <p class="text-muted-foreground text-xs">
                    {{ $t("Дата рождения") }}
                  </p>
                  <p class="text-foreground text-sm">
                    {{ participantForm.birth_date }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </SectionBlock>

        <!-- Адрес регистрации — только для НЕ-self (self подтягивает из ГБД ФЛ). -->
        <SectionBlock
          v-if="
            !actsForLegal &&
            !isSelf &&
            participantForm.identifier?.length === 12
          "
          :title="$t('Адрес регистрации')"
        >
          <FormItem float>
            <FormLabel>
              {{ $t("Страна") }}
              <span class="text-red-500">*</span>
            </FormLabel>
            <FormControl>
              <Input v-model="participantForm.reg_address.country" />
            </FormControl>
          </FormItem>
          <FormItem float>
            <FormLabel>
              {{ $t("Область") }}
              <span class="text-red-500">*</span>
            </FormLabel>
            <FormControl>
              <Input v-model="participantForm.reg_address.region" />
            </FormControl>
          </FormItem>
          <FormItem float>
            <FormLabel>
              {{ $t("Район") }}
              <span class="text-red-500">*</span>
            </FormLabel>
            <FormControl>
              <Input v-model="participantForm.reg_address.district" />
            </FormControl>
          </FormItem>
          <FormItem float>
            <FormLabel>
              {{ $t("Город") }}
              <span class="text-red-500">*</span>
            </FormLabel>
            <FormControl>
              <Input v-model="participantForm.reg_address.city" />
            </FormControl>
          </FormItem>
          <FormItem float>
            <FormLabel>
              {{ $t("Улица") }}
              <span class="text-red-500">*</span>
            </FormLabel>
            <FormControl>
              <Input v-model="participantForm.reg_address.street" />
            </FormControl>
          </FormItem>
          <FormItem float>
            <FormLabel>
              {{ $t("Дом") }}
              <span class="text-red-500">*</span>
            </FormLabel>
            <FormControl>
              <Input v-model="participantForm.reg_address.building" />
            </FormControl>
          </FormItem>
          <FormItem float>
            <FormLabel>{{ $t("Корпус") }}</FormLabel>
            <FormControl>
              <Input v-model="participantForm.reg_address.corpus" />
            </FormControl>
          </FormItem>
          <FormItem float>
            <FormLabel>
              {{ $t("Квартира") }}
              <span class="text-red-500">*</span>
            </FormLabel>
            <FormControl>
              <Input v-model="participantForm.reg_address.flat" />
            </FormControl>
          </FormItem>
        </SectionBlock>

        <!-- Адрес рождения — только в режиме INDIVIDUAL и не для self. -->
        <SectionBlock
          v-if="
            !actsForLegal &&
            !isSelf &&
            participantForm.identifier?.length === 12
          "
          :title="$t('Адрес рождения')"
        >
          <FormItem float>
            <FormLabel>
              {{ $t("Страна") }}
              <span class="text-red-500">*</span>
            </FormLabel>
            <FormControl>
              <Input v-model="participantForm.birth_address.country" />
            </FormControl>
          </FormItem>
          <FormItem float>
            <FormLabel>
              {{ $t("Область") }}
              <span class="text-red-500">*</span>
            </FormLabel>
            <FormControl>
              <Input v-model="participantForm.birth_address.region" />
            </FormControl>
          </FormItem>
          <FormItem float>
            <FormLabel>
              {{ $t("Район") }}
              <span class="text-red-500">*</span>
            </FormLabel>
            <FormControl>
              <Input v-model="participantForm.birth_address.district" />
            </FormControl>
          </FormItem>
          <FormItem float>
            <FormLabel>
              {{ $t("Город") }}
              <span class="text-red-500">*</span>
            </FormLabel>
            <FormControl>
              <Input v-model="participantForm.birth_address.city" />
            </FormControl>
          </FormItem>
        </SectionBlock>
      </template>

      <!-- ===== Режим ЮЛ (действует от имени юридического лица) ===== -->
      <template v-else>
        <div class="flex flex-col gap-3 pt-3">
          <!-- Не руководитель: добавить можно только свою организацию -->
          <div
            v-if="!selfUserBin"
            class="flex gap-3 p-3 rounded-lg bg-yellow-100 items-start"
          >
            <svg-icon
              name="huge-warning"
              class="text-yellow-500 w-5 h-5 flex-shrink-0"
            />
            <span class="text-sm">
              {{
                $t(
                  "Добавить можно только организацию, руководителем которой вы являетесь."
                )
              }}
            </span>
          </div>

          <!-- Нет доступа к сервису ГБД ЮЛ -->
          <div
            v-else-if="!canSearchLegal"
            class="flex gap-3 p-3 rounded-lg bg-yellow-100 items-start"
          >
            <svg-icon
              name="huge-warning"
              class="text-yellow-500 w-5 h-5 flex-shrink-0"
            />
            <span class="text-sm">{{ warningMessage }}</span>
          </div>

          <!-- Загрузка схемы сервиса -->
          <div
            v-else-if="serviceLoading"
            class="flex justify-center items-center py-4"
          >
            <Spinner size="sm" />
          </div>

          <!-- БИН и ИИН руководителя — из сертификата (только чтение) -->
          <template v-else>
            <FormItem float>
              <FormLabel>
                {{ $t("БИН") }}
                <span class="text-red-500">*</span>
              </FormLabel>
              <FormControl>
                <Input
                  v-model="dynamicForm.business_identifier"
                  maxlength="12"
                  readonly
                />
              </FormControl>
            </FormItem>

            <FormItem float>
              <FormLabel>
                {{ $t("ИИН руководителя") }}
                <span class="text-red-500">*</span>
              </FormLabel>
              <FormControl>
                <Input v-model="participantForm.identifier" maxlength="12" />
              </FormControl>
              <p v-if="directorIinMismatch" class="text-red-500 text-xs mt-1">
                {{
                  $t(
                    "ИИН должен совпадать с ИИН руководителя из вашего сертификата ЭЦП — добавить чужую организацию нельзя."
                  )
                }}
              </p>
            </FormItem>

            <Button
              variant="outline"
              class="w-full flex items-center justify-center"
              :disabled="searching || serviceLoading || directorIinMismatch"
              @click="searchCompany"
            >
              <template v-if="searching && lastSearchCode === SERVICE_GBDUL">
                <Spinner size="sm" class="mr-2" />
                {{ $t("Поиск...") }}
              </template>
              <template v-else>
                {{ $t("Запросить данные") }}
              </template>
            </Button>

            <!-- Карточка результата — только из ответа сервиса -->
            <div class="flex flex-col gap-4 mt-2">
              <template v-if="searching && lastSearchCode === SERVICE_GBDUL">
                <p class="text-muted-foreground text-sm font-medium">
                  {{ $t("Идет поиск по базам данных...") }}
                </p>
                <Skeleton class="h-28 w-full" />
              </template>

              <template v-else-if="searchCompleted">
                <p class="text-muted-foreground text-sm font-medium">
                  {{
                    serviceResponseData
                      ? $t("Организация найдена")
                      : $t("Организация не найдена")
                  }}
                </p>

                <div
                  v-if="serviceResponseData"
                  class="p-4 rounded-2xl border border-border flex gap-4 items-start"
                >
                  <div
                    class="w-16 h-[78px] rounded-xl border border-border flex items-center justify-center bg-muted flex-shrink-0"
                  >
                    <svg-icon
                      name="huge-organization"
                      class="w-6 h-6 text-muted-foreground"
                    />
                  </div>
                  <div class="flex flex-col gap-2 flex-1 min-w-0">
                    <div v-if="legalCompanyName">
                      <p class="text-muted-foreground text-xs">
                        {{ $t("Организация") }}
                      </p>
                      <p class="text-foreground text-sm break-words">
                        {{ legalCompanyName }}
                      </p>
                    </div>

                    <div v-if="dynamicForm.business_identifier">
                      <p class="text-muted-foreground text-xs">
                        {{ $t("БИН") }}
                      </p>
                      <p class="text-foreground text-sm">
                        {{ dynamicForm.business_identifier }}
                      </p>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </template>
        </div>
      </template>
    </div>

    <template #footer>
      <div class="flex-1"></div>
      <Button variant="outline" @click="close">
        {{ $t("Отмена") }}
      </Button>
      <Button
        class="ml-2"
        variant="default"
        :disabled="isSaveDisabled"
        @click="save"
      >
        {{ isEdit ? $t("Сохранить") : $t("Добавить") }}
      </Button>
    </template>
  </FormSheet>
</template>
